/**
 * NotificationContext — Global notification state with WebSocket (Story 2.5)
 *
 * Manages: unread count, notification list, WS connection e o aviso ao vivo (parte D1: o
 * avisador decide tela, título e som pelo nível `aviso` que o servidor manda no evento).
 * D2 (passo 14): a mensagem nova do chat (`chat:mensagem_nova`) tem o seu avisador; a linha de
 * menção das conversas do chat só conta no sino, quem anuncia é a mensagem.
 * Pattern: follows MaintenanceContext.tsx / AuthContext.tsx.
 */

import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { WebSocketStatus } from "@/hooks/useWebSocket";
import { useRealtimeNotifications } from "@/hooks/useRealtimeNotifications";
import { isChatRoomsListKey, MESSAGES_KEY } from "@/hooks/useChat";
import { ATRASO_REINVALIDACAO_MS } from "@/hooks/useLeituraDaSala";
import { getApiUrl, getWsUrl } from "@/config/api";
import { mostrarAvisoNaTela, type AcoesDoAviso } from "@/components/notifications/AvisoNaTela";
import {
  lembreteResolvidoFora,
  lembreteVoltouATocar,
} from "@/components/notifications/lembretesResolvidosFora";
import { caminhoDaConversa } from "@/components/notifications/auxiliaresDoAviso";
import { acaoDoLembreteForaDaTela } from "@/hooks/useLembretes";
import { silenciarConversa } from "@/hooks/useSilenciarConversa";
import { useMontagemDoAviso } from "@/hooks/useMontagemDoAviso";
import { anunciar } from "@/lib/avisos/avisador";
import {
  avisarVoltaDoSino,
  ehEventoDeChamada,
  emitirEventoDeChamada,
} from "@/lib/chamadas/eventosDaChamada";
import {
  anunciarMensagem,
  mencaoAnunciaNaTela,
  type MensagemNova,
} from "@/lib/avisos/avisoDaMensagem";
import { enqueue, subscribe } from "@/lib/offline-queue";
import type { TransportMode } from "@/lib/realtime-transport";
import { comLembreteNaMensagem, CHAVE_DO_PAINEL } from "@/lib/lembretes/cache";
import { RAIZ_DA_AGENDA } from "@/lib/calendar/chaves";
import type { ChatMessage, ChatRoom } from "@/types/chat";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1";

/**
 * Quantos ids de notificação o sino lembra para não contar duas vezes
 * (parte B, 30/09/2026). A lista do sino mostra 8; 200 cobre com folga as
 * repetições de uma reconexão.
 */
const LIMITE_DE_VISTOS = 200;

/**
 * Quantas ligações mensagem → linha de menção do sino o contexto lembra (F1 da revisão final do
 * D2). A linha chega segundos antes da mensagem; 50 cobre com folga uma rajada de menções.
 */
const LIMITE_DE_MENCOES = 50;

/**
 * Quantas vezes `refreshUnreadCount` refaz o pedido quando um `notification:new` chega com a
 * resposta em voo (achado A7 do E2E da D1). Passou disso, vale a resposta: um fluxo contínuo de
 * avisos não pode manter o sino pedindo para sempre.
 */
const TENTATIVAS_DA_CONTAGEM = 3;

// ─── Types ────────────────────────────────────────────────────────────────

export interface NotificationInboxItem {
  id: string;
  title: string;
  body: string;
  severity: "info" | "warning" | "error" | "critical";
  module: string | null;
  event_type: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  /** Para onde o clique leva (parte C) — só alguns eventos têm destino. */
  link?: string | null;
  /** O `data` do evento ao vivo (parte D1; a D2 usa o `message_id` da menção). O REST não manda. */
  data?: Record<string, unknown> | null;
}

interface NotificationContextValue {
  notifications: NotificationInboxItem[];
  unreadCount: number;
  isLoading: boolean;
  wsStatus: WebSocketStatus;
  markAsRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, getToken, isAuthenticated } = useAuth();
  const qc = useQueryClient();
  const [notifications, setNotifications] = useState<NotificationInboxItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const markAsReadRef = useRef<(id: string) => Promise<void>>();
  // A lista do sino em ref: o `markAsRead` precisa saber, na hora, se o id está nela.
  const notificationsRef = useRef<NotificationInboxItem[]>([]);
  // Marcações como lida com o PATCH em voo, e se alguma foi de um aviso FORA da lista local
  // (ver `markAsRead`): o contador só se corrige pelo servidor quando todas terminam.
  const marcacoesEmVooRef = useRef(0);
  const recontarAoFimRef = useRef(false);

  // Parte B (30/09/2026): ids já contados. O mesmo `notification:new` pode
  // chegar duas vezes (a aba no WS e no SSE durante a volta ao WS; a lista do
  // REST e o evento ao vivo da mesma notificação) — sem isto o sino somava em
  // dobro.
  const vistosRef = useRef<Set<string>>(new Set());
  // F1 (revisão final do D2): `message_id` → id da linha de menção do sino. Nas conversas do chat
  // a linha NÃO anuncia (quem anuncia é o `chat:mensagem_nova`), então o cartão da mensagem não
  // conhece o id dela; o contexto casa as duas pelo `message_id` para o "Abrir" do cartão baixar
  // a linha do sino. ⚠️ É um ref escrito NA HORA, não a busca em `notificationsRef`: aquele só é
  // atualizado no render, e a linha e a mensagem podem chegar em dois eventos seguidos do WS sem
  // um render no meio (a lista do sino também guarda só 8 avisos).
  const mencaoDaMensagemRef = useRef<Map<string, string>>(new Map());
  const lembrarVisto = useCallback((id: string) => {
    const vistos = vistosRef.current;
    vistos.delete(id);
    vistos.add(id);
    if (vistos.size > LIMITE_DE_VISTOS) {
      const maisAntigo = vistos.values().next().value;
      if (maisAntigo !== undefined) vistos.delete(maisAntigo);
    }
  }, []);

  const authHeaders = useCallback(() => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : {};
  }, [getToken]);

  // ── REST fetchers ──────────────────────────────────────────────────

  const fetchInbox = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await fetch(`${API_BASE_URL}/notifications/inbox?limit=8`, {
        headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const itens: NotificationInboxItem[] = (data.items || []).map((item: any) => ({
          ...item,
          severity: item.severity || "info",
        }));
        itens.forEach((item) => lembrarVisto(item.id));
        setNotifications(itens);
      }
    } catch (err) {
      console.error("[NotificationContext] fetchInbox error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [authHeaders, lembrarVisto]);

  // Achado A7 (E2E da D1, 02/10/2026): quantos `notification:new` já entraram no contador.
  // O `GET /unread-count` roda ao montar, ao reconectar e ao voltar à aba; se um evento chega
  // com a resposta em voo e o servidor a calculou antes de gravar a notificação, a resposta
  // traz o valor de antes e sobrescrevia o `+1` do evento (o sino parado até recarregar).
  const eventosContadosRef = useRef(0);

  // Passo 14: a reconferência das listas de salas que segue um `chat:sala_lida`. Em ref para que
  // um evento novo reinicie a espera (uma só reconferência) e o desmonte a cancele.
  const reconferenciaDasSalasRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (reconferenciaDasSalasRef.current !== null) {
        clearTimeout(reconferenciaDasSalasRef.current);
        reconferenciaDasSalasRef.current = null;
      }
    },
    []
  );

  const refreshUnreadCount = useCallback(async () => {
    try {
      for (let tentativa = 1; tentativa <= TENTATIVAS_DA_CONTAGEM; tentativa += 1) {
        const antes = eventosContadosRef.current;
        const res = await fetch(`${API_BASE_URL}/notifications/unread-count`, {
          headers: authHeaders(),
        });
        if (!res.ok) return;
        const data = await res.json();
        // Um evento atravessou o pedido: a resposta pode ser mais velha que ele. Descarta (o
        // contador segue com o `+1`) e pergunta de novo — o pedido novo nasce DEPOIS do evento,
        // que o servidor publica só depois do commit, então já o conta.
        if (eventosContadosRef.current !== antes && tentativa < TENTATIVAS_DA_CONTAGEM) continue;
        setUnreadCount(data.unread_count);
        return;
      }
    } catch (err) {
      console.error("[NotificationContext] refreshUnreadCount error:", err);
    }
  }, [authHeaders]);

  const markAsRead = useCallback(
    async (id: string) => {
      // O sino guarda só 8 avisos (`slice(0, 8)`): um aviso fora dessa lista não desconta no
      // contador abaixo (o `prev.find` não o acha), e o aviso agrupado da tela marca o grupo
      // inteiro (decisão do Augusto, 03/10/2026). Para esses, o contador se corrige pelo
      // servidor depois do PATCH; descontar às cegas contaria a menos se o aviso já tivesse
      // sido lido em outro aparelho.
      const estavaNaLista = notificationsRef.current.some((n) => n.id === id);

      // Optimistic update — check unread status via setState callback to avoid stale closure
      setNotifications((prev) => {
        const target = prev.find((n) => n.id === id && !n.is_read);
        if (target) setUnreadCount((c) => Math.max(0, c - 1));
        return prev.map((n) =>
          n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
        );
      });

      // Story X1.3.3 — offline: mantém otimista + enfileira (flush dá o PATCH
      // ao reconectar). É idempotente (marcar lida 2x = mesmo estado).
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        await enqueue("mark_read", { id }).catch((e: unknown) =>
          console.debug("[NotificationContext] enqueue mark_read falhou:", e)
        );
        return;
      }

      marcacoesEmVooRef.current += 1;
      try {
        const res = await fetch(`${API_BASE_URL}/notifications/${id}/read`, {
          method: "PATCH",
          headers: authHeaders(),
        });
        if (!res.ok) {
          await refreshUnreadCount();
          await fetchInbox();
        } else if (!estavaNaLista) {
          recontarAoFimRef.current = true;
        }
      } catch {
        // Erro de rede → enfileira pra retry no reconnect (mantém otimista).
        await enqueue("mark_read", { id }).catch((e: unknown) =>
          console.debug("[NotificationContext] enqueue mark_read falhou:", e)
        );
      } finally {
        marcacoesEmVooRef.current -= 1;
        // UMA consulta, depois do ÚLTIMO PATCH do grupo: a que sai antes poderia contar os que
        // ainda estão em voo. Reaproveita o `refreshUnreadCount` (e a proteção do A7).
        if (marcacoesEmVooRef.current === 0 && recontarAoFimRef.current) {
          recontarAoFimRef.current = false;
          await refreshUnreadCount();
        }
      }
    },
    [authHeaders, refreshUnreadCount, fetchInbox]
  );

  const markAllRead = useCallback(async () => {
    // Optimistic update
    setUnreadCount(0);
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
    );

    // Story X1.3.3 — offline: mantém otimista + enfileira o batch.
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      await enqueue("mark_all_read", {}).catch((e: unknown) =>
        console.debug("[NotificationContext] enqueue mark_all_read falhou:", e)
      );
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/notifications/mark-all-read`, {
        method: "POST",
        headers: authHeaders(),
      });
      if (!res.ok) {
        await refreshUnreadCount();
        await fetchInbox();
      }
    } catch {
      await enqueue("mark_all_read", {}).catch((e: unknown) =>
        console.debug("[NotificationContext] enqueue mark_all_read falhou:", e)
      );
    }
  }, [authHeaders, refreshUnreadCount, fetchInbox]);

  // Keep ref in sync for use inside WS handler without dependency
  markAsReadRef.current = markAsRead;
  notificationsRef.current = notifications;

  // Parte D1 / peça M do D3: o 1º gesto da aba libera o áudio e a põe na fila do som, a trava é solta ao desmontar e o
  // título conta o que chegou com a aba oculta. Mora num hook para o provedor do portal chamar o mesmo.
  useMontagemDoAviso();

  // As ações do aviso na tela (D1 e D2), as mesmas para o aviso do sino e o da mensagem. O
  // `queryClient` mora aqui: o cartão da conversa não é um componente de tela, e o Toaster fica
  // FORA do `NotificationProvider`. `markAsReadRef` e o `qc` são estáveis, então o objeto também.
  const acoesDoAviso = useMemo<AcoesDoAviso>(
    () => ({
      marcarLida: (id) => void markAsReadRef.current?.(id),
      silenciarConversa: (roomId, silenciar) => silenciarConversa(qc, roomId, silenciar),
      // Passo 18, PR 2: o cartão do lembrete conclui/adia pela rota autenticada (o Toaster fica fora do provider).
      concluirLembrete: (l) => acaoDoLembreteForaDaTela(qc, "concluir", l),
      adiarLembrete: (l) => acaoDoLembreteForaDaTela(qc, "adiar", l),
    }),
    [qc]
  );

  // ── Realtime transport (WS-first + SSE fallback — Story E1.5.2) ────

  const token = getToken();
  const wsUrl = token ? getWsUrl(`/api/v1/ws/notifications?token=${token}`) : "";
  const sseUrl = token ? getApiUrl("/sse/notifications") : "";

  const handleEvent = useCallback(
    (data: any) => {
      if (!data?.type) return;

      // Ligações do chat (Task 10, 24/10/2026): `chamada:chegando|parou|atendida|encerrada`, no canal
      // pessoal. Vão direto ao barramento (o `CallProvider` as ouve) e param aqui: não são
      // notificação do sino, não anunciam e não tocam nas listas de salas. O `chamada:sala` é do
      // canal da sala (o `useChat` o trata) e `ehEventoDeChamada` o recusa.
      if (ehEventoDeChamada(data)) {
        emitirEventoDeChamada(data);
        return;
      }

      if (data.type === "notification:badge_update") {
        const dados = data.data ?? {};
        // Passo 14 (07/10/2026): o evento também sai quando a pessoa lê noutra aba ou aparelho
        // (`lidas` = os ids, `todas` = marcou tudo). Conta como evento na proteção do A7: um
        // `GET /unread-count` que atravessou este evento traz o número de antes e não pode vencer.
        eventosContadosRef.current += 1;
        const lidas: string[] | null = Array.isArray(dados.lidas) ? dados.lidas : null;
        const deLeitura = dados.todas === true || lidas !== null;
        const n: number = dados.unread_count ?? 0;
        // Leitura só BAIXA o número: os PATCHes do aviso agrupado saem em paralelo e as contagens
        // chegam fora de ordem. O evento do conectar (sem `lidas`/`todas`) define o número.
        setUnreadCount((atual) => (deLeitura ? Math.min(atual, n) : n));
        if (dados.todas === true || lidas?.length) {
          const agora = new Date().toISOString();
          setNotifications((prev) =>
            prev.map((item) =>
              !item.is_read && (dados.todas === true || lidas?.includes(item.id))
                ? { ...item, is_read: true, read_at: agora }
                : item
            )
          );
        }
        return;
      }

      // Passo 14 (07/10/2026): a pessoa leu a conversa noutra aba ou aparelho. Zera aqui sem
      // `GET`: a lista vem do Postgres, que só recebe a leitura ~1 s depois (worker do Redis) — o
      // refetch traria o número velho. Mensagem que chegou DEPOIS da leitura continua contando.
      if (data.type === "chat:sala_lida") {
        const lidaEm = Date.parse(data.last_read_at ?? "");
        if (!data.room_id || Number.isNaN(lidaEm)) return;
        qc.setQueriesData<ChatRoom[]>({ predicate: isChatRoomsListKey }, (antes) =>
          antes?.map((sala) => {
            if (sala.id !== data.room_id) return sala;
            const ultima = sala.last_interaction_at ? Date.parse(sala.last_interaction_at) : 0;
            return ultima > lidaEm
              ? sala
              : { ...sala, unread_count: 0, has_unread_mentions: false };
          })
        );
        // Depois da janela do worker, reconfere com o servidor: um refetch que tenha caído nela
        // (volta à aba, outra mensagem) não pode deixar o número velho de pé. Só as LISTAS: o
        // prefixo ["chat","rooms"] recarregaria também as mensagens da sala aberta. Mesma espera do
        // `useLeituraDaSala`; um evento novo reinicia a contagem (uma reconferência só).
        if (reconferenciaDasSalasRef.current !== null) {
          clearTimeout(reconferenciaDasSalasRef.current);
        }
        reconferenciaDasSalasRef.current = setTimeout(() => {
          reconferenciaDasSalasRef.current = null;
          qc.invalidateQueries({ predicate: isChatRoomsListKey });
        }, ATRASO_REINVALIDACAO_MS);
        return;
      }

      // Story E0.1.3 (FR-E0-5): fan-out de invalidate da lista de salas do
      // chat. Desde a parte B (30/09/2026) o backend (chat.py send_message)
      // manda um evento por membro, no canal pessoal dele, com
      // `recipient_user_ids` = só o próprio id do destinatário; o filtro
      // abaixo fica como defesa e por compatibilidade (aba com JS antigo,
      // evento de formato antigo). Invalida as LISTAS de salas. Sem isso, badges
      // de salas FECHADAS só aparecem em reload manual.
      if (data.type === "chat:rooms_invalidate") {
        const recipients = data.recipient_user_ids;
        if (Array.isArray(recipients) && user?.user_id && recipients.includes(user.user_id)) {
          // Passo 14: as listas, não o prefixo ["chat","rooms"]: ele pegava também as mensagens, os
          // membros e o read-state da sala ABERTA, recarregados a cada mensagem de OUTRA sala.
          qc.invalidateQueries({ predicate: isChatRoomsListKey });
          // ⚠️ Rede de segurança da sala aberta: o WebSocket da própria sala não reconecta nem faz
          // backfill (`useChatWebSocket`) e a aba não relê ao voltar o foco, então, com ele caído,
          // este aviso (o servidor o manda a todo membro, inclusive a quem está com a sala aberta) é
          // a única via de a mensagem chegar. Só as MENSAGENS da sala do evento: o GET roda se a
          // query está ativa (a sala aberta); a de outra sala só fica marcada. Membros e read-state
          // seguem de fora.
          if (typeof data.room_id === "string" && data.room_id !== "") {
            qc.invalidateQueries({ queryKey: MESSAGES_KEY(data.room_id) });
          }
        }
        return;
      }

      // D2 (passo 14, 07/10/2026): a mensagem nova de uma conversa do chat (DM, grupo, setor), um
      // evento por membro no canal da pessoa. `data` é aninhado como o do `notification:new`.
      // Nas salas que avisam o backend manda ESTE evento no lugar do `chat:rooms_invalidate`.
      if (data.type === "chat:mensagem_nova") {
        const m: MensagemNova | undefined = data.data;
        if (!m?.room_id) return;
        // Defesa, como no `notification:new`: o evento é da pessoa que o recebe.
        if (m.user_id && user?.user_id && m.user_id !== user.user_id) return;
        // O transporte pode entregar o mesmo evento duas vezes (a aba no WS e no SSE durante a
        // volta ao WS, como no `notification:new`): sem isto o cartão agrupava "2 mensagens
        // novas" para uma só. Repetido não faz nada, nem refaz as listas (o 1º já o fez, depois do
        // commit; refazer seria um GET /chat/rooms a mais por membro). O prefixo `msg:` separa
        // estes ids dos das notificações, que moram no mesmo conjunto (limite de 200).
        if (m.message_id) {
          const chave = `msg:${m.message_id}`;
          if (vistosRef.current.has(chave)) return;
          lembrarVisto(chave);
        }

        // O contador e a ordem das conversas mudaram: refaz as LISTAS (nunca o prefixo, que
        // pegaria também os membros e o read-state da sala aberta).
        qc.invalidateQueries({ predicate: isChatRoomsListKey });
        // ⚠️ A rede de segurança da sala aberta, a mesma do `chat:rooms_invalidate` abaixo: este
        // evento o substitui nas salas que avisam, e o WebSocket da sala não reconecta nem faz
        // backfill — com ele caído, esta é a única via de a mensagem chegar. Só as mensagens da
        // sala do evento (o GET roda se a query está ativa).
        qc.invalidateQueries({ queryKey: MESSAGES_KEY(m.room_id) });

        // A linha de menção desta mensagem no sino, se já chegou (ela vem ANTES): o "Abrir" do
        // cartão a marca como lida. Só quando a mensagem cita a pessoa.
        const idDoSino = m.mencionou_voce
          ? mencaoDaMensagemRef.current.get(m.message_id)
          : undefined;

        // O avisador decide (sala à vista, nível, som); a função recebida monta o aviso.
        anunciarMensagem(m, () =>
          mostrarAvisoNaTela(
            {
              // O id da mensagem nunca vai para o `marcarLida`: o cartão só marca itens do sino.
              id: m.message_id,
              title: m.room_type === "DM" ? m.author_name : `${m.author_name} · ${m.sala}`,
              body: m.preview,
              severity: "info",
              // Menção vale mais que o tipo da sala; sem ela, DM é "diretas" e o resto "grupos".
              categoria: m.mencionou_voce
                ? "chat_mencoes"
                : m.room_type === "DM"
                  ? "chat_diretas"
                  : "chat_grupos",
              link: caminhoDaConversa(m.room_id),
              created_at: m.created_at || new Date().toISOString(),
              conversa: {
                room_id: m.room_id,
                room_type: m.room_type,
                sala: m.sala,
                author_id: m.author_id,
                author_name: m.author_name,
                mencao: m.mencionou_voce === true,
                silenciada: m.silenciada === true,
                ...(idDoSino ? { idDoSino } : {}),
              },
            },
            acoesDoAviso
          )
        );
        return;
      }

      // Passo 18, PR 2: um lembrete da pessoa mudou (outra aba, o botão do push, a varredura que tocou). Troca a
      // marca na bolha da sala de notas e refaz o painel e as LISTAS (os números do botão e da linha) e, no PR 3, a
      // Agenda (que mostra os lembretes e não tem WebSocket: com `/calendar` aberta, o ⏰ só viraria 🔔/✓ ao voltar o
      // foco ou depois de 30 s). O aviso de "tocou" chega à parte, pelo `notification:new` (categoria "lembretes").
      if (data.type === "lembrete:atualizado") {
        const d = data.data ?? {};
        if (d.user_id && user?.user_id && d.user_id !== user.user_id) return;
        // O cartão do aviso na tela fica aberto até a pessoa agir: um lembrete que saiu de "tocou" (concluído, adiado,
        // remarcado ou cancelado pela bolha, pelo painel, por outra aba ou pelo push) sai dele; um que tocou de novo volta.
        const idDoLembrete = d.lembrete?.id;
        if (typeof idDoLembrete === "string") {
          if (d.lembrete.status === "tocou") lembreteVoltouATocar(idDoLembrete);
          else lembreteResolvidoFora(idDoLembrete);
        }
        if (typeof d.room_id === "string" && typeof d.message_id === "string") {
          qc.setQueryData<ChatMessage[]>(MESSAGES_KEY(d.room_id), (old) =>
            comLembreteNaMensagem(old, d.message_id, d.lembrete ?? null)
          );
        }
        qc.invalidateQueries({ queryKey: CHAVE_DO_PAINEL });
        qc.invalidateQueries({ predicate: isChatRoomsListKey });
        qc.invalidateQueries({ queryKey: RAIZ_DA_AGENDA });
        return;
      }

      if (data.type === "notification:new") {
        const payload = data.data;
        // AC7: filter by user_id (broadcast is per company)
        if (payload.user_id && user?.user_id && payload.user_id !== user.user_id) {
          return; // Not for this user
        }
        if (payload.id) {
          if (vistosRef.current.has(payload.id)) return; // já contada
          lembrarVisto(payload.id);
        }

        const item: NotificationInboxItem = {
          id: payload.id,
          title: payload.title,
          body: payload.body,
          severity: payload.severity || "info",
          module: payload.module || null,
          event_type: payload.event_type || "notification",
          is_read: false,
          read_at: null,
          created_at: payload.created_at || new Date().toISOString(),
          link: payload.link ?? null,
          data: payload.data ?? null,
        };

        setNotifications((prev) => [item, ...prev].slice(0, 8));
        setUnreadCount((c) => c + 1);
        eventosContadosRef.current += 1;

        // D2: a linha de menção das conversas do chat entra no sino (acima) mas NÃO anuncia: quem
        // anuncia é o `chat:mensagem_nova` da mesma mensagem, que traz o maior nível. ⚠️ O
        // `room_type` mora em `payload.data` (o `data` DA LINHA), não no payload: passar o payload
        // inteiro o deixa indefinido e a menção anuncia em dobro, sem erro.
        if (!mencaoAnunciaNaTela(payload.event_type, payload.data)) {
          // F1: lembra qual linha do sino é desta mensagem, para o cartão dela baixar a linha.
          const idDaMensagem = payload.data?.message_id;
          if (typeof idDaMensagem === "string" && idDaMensagem !== "") {
            const mencoes = mencaoDaMensagemRef.current;
            mencoes.delete(idDaMensagem);
            mencoes.set(idDaMensagem, item.id);
            if (mencoes.size > LIMITE_DE_MENCOES) {
              const maisAntiga = mencoes.keys().next().value;
              if (maisAntiga !== undefined) mencoes.delete(maisAntiga);
            }
          }
          return;
        }

        // Passo 18, PR 2: o aviso do lembrete leva o lembrete (o cartão age por ele). Só com os ids no `data` da linha.
        const daLinha = (payload.data ?? {}) as Record<string, unknown>;
        const lembrete =
          payload.categoria === "lembretes" &&
          typeof daLinha.lembrete_id === "string" &&
          typeof daLinha.message_id === "string"
            ? {
                id: daLinha.lembrete_id,
                message_id: daLinha.message_id,
                room_id: typeof daLinha.room_id === "string" ? daLinha.room_id : null,
              }
            : undefined;

        // Toque novo: o lembrete volta a esperar a pessoa, mesmo que o `lembrete:atualizado` dele chegue depois.
        if (lembrete) lembreteVoltouATocar(lembrete.id);

        // Parte D1 (01/10/2026): o servidor manda o nível da pessoa (`aviso`); o avisador
        // decide tela, título e som. Sem `aviso` (deploy), vale a regra de antes.
        anunciar({ aviso: payload.aviso, severity: item.severity }, () =>
          mostrarAvisoNaTela(
            {
              id: item.id,
              title: item.title,
              body: item.body,
              severity: item.severity,
              categoria: payload.categoria ?? null,
              link: item.link,
              created_at: item.created_at,
              ...(lembrete ? { lembrete } : {}),
            },
            acoesDoAviso
          )
        );
      }
    },
    [user?.user_id, qc, lembrarVisto, acoesDoAviso]
  );

  const { mode: transportMode } = useRealtimeNotifications({
    wsUrl,
    sseUrl,
    getAuthToken: getToken,
    onEvent: handleEvent,
    enabled: !!token,
  });

  // AC4 preservada (Story 2.5): refresh inbox quando o transport
  // (re)conecta — agora indiferente a WS ou SSE.
  const lastConnectedRef = useRef<TransportMode>("disconnected");
  // Passo 14: marcado na 1ª conexão da página. Só a RE-conexão acerta a lista de salas — na primeira
  // a própria tela acabou de buscá-la, e invalidar de novo faria cada carga pedir `/chat/rooms` 2x.
  const jaConectouRef = useRef(false);
  useEffect(() => {
    const wasConnected = lastConnectedRef.current === "ws" || lastConnectedRef.current === "sse";
    const isConnected = transportMode === "ws" || transportMode === "sse";
    if (!wasConnected && isConnected) {
      // Parte B: a lista E a contagem — o que chegou durante a queda não veio
      // ao vivo (o pub/sub do Redis não guarda nada).
      fetchInbox();
      refreshUnreadCount();
      // Idem para as conversas: o `chat:rooms_invalidate` perdido na queda não volta (follow-up
      // do #744), e o contador das salas ficaria velho até um reload. Só as listas: o prefixo
      // ["chat","rooms"] recarregaria também as mensagens e os membros da sala aberta.
      if (jaConectouRef.current) {
        qc.invalidateQueries({ predicate: isChatRoomsListKey });
        // Idem para a ligação na tela: o `chamada:atendida`/`chamada:encerrada` perdido na queda não volta, e
        // quem liga ficaria em "Chamando…". O `CallProvider` confere o `/estado` (R2).
        avisarVoltaDoSino();
      }
      jaConectouRef.current = true;
    }
    lastConnectedRef.current = transportMode;
  }, [transportMode, fetchInbox, refreshUnreadCount, qc]);

  // Backwards-compat shim: consumidores que ainda esperam `wsStatus`
  // (enum legado) recebem mapeamento direto. Story E1.5.3 (badge) lê
  // o `transportMode` cru via `useTransportMode()` em vez disso.
  const wsStatus: WebSocketStatus =
    transportMode === "ws" || transportMode === "sse"
      ? WebSocketStatus.CONNECTED
      : transportMode === "connecting"
        ? WebSocketStatus.CONNECTING
        : WebSocketStatus.DISCONNECTED;

  // ── Initial fetch & visibility change ──────────────────────────────

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchInbox();
    refreshUnreadCount();
  }, [isAuthenticated, fetchInbox, refreshUnreadCount]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && isAuthenticated) {
        refreshUnreadCount();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [isAuthenticated, refreshUnreadCount]);

  // Story X1.3.3 — reconcilia com o servidor após o flush da fila offline (o
  // PATCH/POST de mark-read enfileirado é replayado ao reconectar). Só refaz
  // quando online; fetchers engolem erro, então um refetch falho não reverte
  // o estado otimista. DEBOUNCE: o queue dispara `notify()` por item removido
  // num flush (N itens → N eventos) e em enqueues não-relacionados (ex.: chat) —
  // coalescemos num único refetch ~400ms após o último evento.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsub = subscribe(() => {
      if (typeof navigator === "undefined" || !navigator.onLine || !isAuthenticated) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void refreshUnreadCount();
        void fetchInbox();
      }, 400);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsub();
    };
  }, [isAuthenticated, refreshUnreadCount, fetchInbox]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isLoading,
        wsStatus,
        markAsRead,
        markAllRead,
        refreshUnreadCount,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────

export const useNotifications = (): NotificationContextValue => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
};
